// src/App.tsx
import React, { Suspense } from "react";
import { MantineProvider } from "@mantine/core";
import { QueryClientProvider } from "@tanstack/react-query";
import theme from "./theme";
import {
  BrowserRouter as Router,
  useLocation,
  Routes,
  Route,
  Navigate,
  type Location,
} from "react-router-dom";

import ProtectedRoute from "./components/ProtectedRoute";
import RequireOnboarding from "./components/RequireOnboarding";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { useAuth } from "./context/AuthContext";
import { WeekProvider } from "./context/WeekContext";
import { queryClient } from "./lib/queryClient";
import JournalPage from "./pages/JournalPage";
import GPSummary from "./pages/GPSummary";
import Recovery from "./pages/Recovery";
import SignUp from "./pages/SignUp";

import Login from "./pages/Login";
import Ftu from "./pages/Ftu";
import MoodCheckin from "./pages/checkins/MoodCheckin";
import BleedingCheckin from "./pages/checkins/BleedingCheckin";
import Profile from "./pages/Profile";
import Questionnaire from "./pages/questionnaire";
import SymptomsCheckin from "./pages/checkins/SymptomsCheckin";
import Dashboard from "./pages/Dashboard";

// ── Lazy-loaded pages (code-split) ──────────────────────────────────────────
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const VerifyEmail = React.lazy(() => import("./pages/VerifyEmail"));
const AntenatalJourney = React.lazy(() => import("./pages/AntenatalJourney"));
const CircumstancesSupport = React.lazy(() => import("./pages/CircumstancesSupport"));
const TermsAndConditions = React.lazy(() => import("./pages/TermsAndConditions"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetLinkSent = React.lazy(() => import("./pages/ResetLinkSent"));
const Player = React.lazy(() => import("./pages/Player"));
const Logout = React.lazy(() => import("./pages/Logout"));
const SeedLessons = React.lazy(() => import("./pages/SeedLessons"));
const RefreshVimeo = React.lazy(() => import("./pages/RefreshVimeo"));
const PersonalDetails = React.lazy(() => import("./pages/PersonalDetails"));
const ChildbirthDetails = React.lazy(() => import("./pages/Childbirthdetails"));
const Appointments = React.lazy(() => import("./pages/appointments"));
const RecoveryGoals = React.lazy(() => import("./pages/RecoveryGoals"));
const ChangePassword = React.lazy(() => import("./pages/ChangePassword"));
const HealthProfile = React.lazy(() => import("./pages/Healthprofile"));
const SavedLessons = React.lazy(() => import("./pages/SavedLessons"));
const PostnatalRedFlags = React.lazy(() => import("./pages/PostnatalRedFlags"));
// Feedback flow
const FeedbackStep1        = React.lazy(() => import("./pages/FeedbackStep1"));
const FeedbackStep2        = React.lazy(() => import("./pages/FeedbackStep2"));
const FeedbackStep3        = React.lazy(() => import("./pages/FeedbackStep3"));
const FeedbackStep4        = React.lazy(() => import("./pages/FeedbackStep4"));
const FeedbackSuccess      = React.lazy(() => import("./pages/FeedbackSuccess"));

// Prefetch the most-visited pages while the browser is idle so navigation feels instant
if (typeof window !== "undefined" && "requestIdleCallback" in window) {
  requestIdleCallback(() => {
    import("./pages/Dashboard");
    import("./pages/Recovery");
    import("./pages/Player");
    import("./pages/Profile");
  });
}

// ── High Quality Loader Component ──────────────────────────────────────────

  const PageLoader = () => (
  

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        /* We use minHeight and height 100% to force mobile browsers to comply */
        height: '100%',
        minHeight: '-webkit-fill-available', 
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647, /* Maximum possible z-index */
        pointerEvents: 'all'
      }}>
        {/* Spinner */}
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />

        {/* Branding */}
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          /* Ensure the body doesn't scroll on mobile while loading */
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );

function AppRoutes() {
  const { user, loading } = useAuth(); // Destructure loading here
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location } | null;
  const backgroundLocation = state?.backgroundLocation;

  // ── AUTH GUARD: This prevents the Landing Page flash ──────────────────────
  if (loading) {
    return <PageLoader />;
  }

  const isAuthed = Boolean(user);
  const isVerified = Boolean(user?.emailVerified);

  return (
    <Suspense fallback={<PageLoader />}>
      {/* When a backgroundLocation exists the main Routes keep rendering the page behind the overlay */}
      <Routes location={backgroundLocation || location}>
        {/* Home: LandingPage or redirect if logged in */}
        <Route
          path="/"
          element={
            !isAuthed ? (
              <LandingPage />
            ) : isVerified ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/verify-email" replace />
            )
          }
        />

        {/* Public auth / legal routes */}
        <Route path="/signup" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/reset-link-sent" element={<ResetLinkSent />} />
        <Route
          path="/verify-email"
          element={isAuthed ? <VerifyEmail /> : <Navigate to="/" replace />}
        />

        {/* FTU route – must be logged in, but does NOT require onboarding completed */}
        <Route
          path="/ftu"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary pageName="Onboarding">
                <Ftu />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Logout */}
        <Route
          path="/logout"
          element={
            <ProtectedRoute>
              <Logout />
            </ProtectedRoute>
          }
        />

        {/* Core app routes – require login AND FTU completed */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Dashboard">
                  <Dashboard />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/antenatal-journey"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Antenatal Journey">
                  <AntenatalJourney />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/circumstances-support"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Circumstances Support">
                  <CircumstancesSupport />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/journal"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Journal">
                  <JournalPage />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />
        <Route
  path="/red-flags"
  element={
    <ProtectedRoute>
      <RequireOnboarding>
        <RouteErrorBoundary pageName="Red Flags">
          <PostnatalRedFlags />
        </RouteErrorBoundary>
      </RequireOnboarding>
    </ProtectedRoute>
  }
/>
        {/* GP Summary page */}
        <Route
          path="/gp-summary"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="GP Summary">
                  <GPSummary />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Profile">
                  <Profile />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/personal-details"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Personal Details">
                  <PersonalDetails />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/childbirth-details"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Childbirth Details">
                  <ChildbirthDetails />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/appointments"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Appointments">
                  <Appointments />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/health-profile"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Health Profile">
                  <HealthProfile />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/recovery-goals"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Recovery Goals">
                  <RecoveryGoals />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/change-password"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Change Password">
                  <ChangePassword />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/saved-lessons"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Saved Lessons">
                  <SavedLessons />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />
        {/* Feedback routes */}
        <Route
          path="/profile/feedback"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Feedback Step 1">
                  <FeedbackStep1 />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/feedback/details"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Feedback Details">
                  <FeedbackStep2 />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/feedback/screenshots"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Feedback Screenshots">
                  <FeedbackStep3 />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/feedback/suggestions"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Feedback Suggestions">
                  <FeedbackStep4 />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/feedback/success"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Feedback Success">
                  <FeedbackSuccess />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/recovery"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Recovery">
                  <Recovery />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        {/* Checkin paths exist in main Routes so direct-navigation still works (no overlay) */}
        <Route
          path="/recovery/checkins/mood"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <MoodCheckin />
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/recovery/checkins/symptoms"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <SymptomsCheckin />
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/recovery/checkins/bleeding"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <BleedingCheckin />
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/questionnaire/:weekId"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Questionnaire">
                  <Questionnaire />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/recovery/week/:week/lesson/:lessonId"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RouteErrorBoundary pageName="Player">
                  <Player />
                </RouteErrorBoundary>
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        {/* Admin / media */}
        <Route
          path="/admin/seed-lessons"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <SeedLessons />
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/refresh-vimeo"
          element={
            <ProtectedRoute>
              <RequireOnboarding>
                <RefreshVimeo />
              </RequireOnboarding>
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={
            <Navigate
              to={isAuthed ? (isVerified ? "/dashboard" : "/verify-email") : "/"}
              replace
            />
          }
        />
      </Routes>

      {/* Overlay routes – rendered ON TOP of the background page */}
      {backgroundLocation && (
        <Routes>
          <Route
            path="/recovery/checkins/mood"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <MoodCheckin />
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recovery/checkins/symptoms"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <SymptomsCheckin />
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recovery/checkins/bleeding"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <BleedingCheckin />
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
        </Routes>
      )}
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <Router>
          <WeekProvider>
            <AppRoutes />
          </WeekProvider>
        </Router>
      </MantineProvider>
    </QueryClientProvider>
  );
}